import React, { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import PhoneCertButton from "./PhoneCertButton";
import LoadingCircle from "./LoadingCircleAnimated";
import { updateProfile } from "../Data/User/Profile";
import { logBehavior } from "../Data/Behavior";
import { useSession } from "../Recoil/Session/Session";
import { useUsers } from "../Recoil/User/User";
import "./AgeVerificationDialog.scss";

const AgeVerificationDialog = ({
    serviceId, 
    titleId, 
    open,
    onClose,
    onSuccess
}: {
    serviceId: string, 
    titleId: string, 
    open: boolean,
    onClose: () => void,
    onSuccess: () => void
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [verificationComplete, setVerificationComplete] = useState(false);
    const { session } = useSession();
    const { refreshUser } = useUsers();

    const handleVerificationSuccess = async (userInfo: { name: string; birthday: string; phoneNumber: string }) => {
        setIsLoading(true);
        setError(null);
        
        try {
            // Update user profile with certification information
            await updateProfile({
                certificatedName: userInfo.name,
                certificatedBirthday: userInfo.birthday,
                certificatedPhoneNumber: userInfo.phoneNumber
            });
            // Refresh user profile data to get certification info
            if (session && session.addressid) {
                refreshUser([session.addressid]);
            }
            
            logBehavior('age-verification-success', { serviceId, titleId, userInfo });
            
            setVerificationComplete(true);
            onSuccess();
        } catch (error) {
            setError("인증 정보 저장에 실패했습니다. 다시 시도해주세요.");
            logBehavior('age-verification-fail', { serviceId, titleId, error });
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerificationFail = (error: any) => {
        setError(error?.message ?? "인증에 실패했습니다. 다시 시도해주세요.");
        logBehavior('age-verification-fail', { serviceId, titleId, error });
    };

    return (
        <Dialog
            className="AgeVerificationDialog"
            open={open}
            onClose={onClose}
            hideBackdrop={false}
            disableScrollLock={true}
        >
            <DialogTitle className="AgeVerificationTitle">
                {"독자님!\n성인 인증이 필요해요."}
            </DialogTitle>
            
            <DialogContent className="AgeVerificationContent">
                {"이 콘텐츠는 성인만 이용할 수 있어요.\n성인인증 완료 후 자유롭게 컨텐츠를 이용해보세요."}
                
                {error && (
                    <div className="ErrorMessage">
                        {error}
                    </div>
                )}
            </DialogContent>
            
            <DialogActions className="AgeVerificationActions">
                <Button 
                    fullWidth 
                    onClick={onClose}
                >
                    {"다음에 인증할게요"}
                </Button>
                
                <PhoneCertButton
                    fullWidth
                    disabled={isLoading || verificationComplete}
                    onSuccess={handleVerificationSuccess}
                    onFail={handleVerificationFail}
                    className="DialogCTA"
                />
            </DialogActions>
            
            <LoadingCircle show={isLoading} />
        </Dialog>
    );
};

export default AgeVerificationDialog;